/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DestroyRef, Component, Inject, OnInit} from '@angular/core';
import * as _ from 'lodash';
import {map} from 'rxjs';

import {OBJECT_TYPE} from '../../common/constants/object-type';
import {buildLastViewedEntries} from './simple-last-viewed.util';
import template from './version-history-page.component.html';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {VersionHistoryApiService} from './version-history-api.service';
import {ObjectDiffService} from '../../common/util/object-diff.service';
import {SignalPageState} from '../../common/controller/signal-page-state';
import {NavigationHistoryService} from '../../../components/util/navigation-history.service';

@Component({
  standalone: false,
  selector: 'adn-version-history-page',
  template
})
export class VersionHistoryPageComponent implements OnInit {
  lastViewed: any[] = [];
  lastViewedObject: any;
  selectFrom = 'LAST_VIEWED';

  readonly page: SignalPageState<any, {id: string; type: string}>;
  get versions(): any[] { return this.page.items(); }
  get totalCount(): number { return this.page.totalCount(); }
  get masquerade(): boolean { return this.versions.some((version) => !!version.masqueraderUser); }
  objectId: string;
  objectType: any;
  objectTypes = OBJECT_TYPE;
  readonly objectTypeKeys = Object.keys(OBJECT_TYPE);

  constructor(
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(ObjectDiffService) private readonly ObjectDiff: any,
    private readonly versionHistoryApi: VersionHistoryApiService,
    destroyRef: DestroyRef,
    navigationHistory: NavigationHistoryService
  ) {
    this.page = new SignalPageState((query, page, size) => this.versionHistoryApi.get(query.id, query.type, page, size).pipe(
      map((data) => ({...data, results: this.parseVersionHistory(data)}))
    ), 30, destroyRef);
    this.lastViewed = buildLastViewedEntries(this.localUserProfile, this.LocalNetworkProfile);
    this.lastViewedObject = _.get(this.lastViewed, [0]);
    this.objectId = navigationHistory.previousParams.id;
    this.objectType = _.find(_.values(OBJECT_TYPE), (ot) => ot.stateName === navigationHistory.previousState?.name);
  }

  ngOnInit(): void {
    this.showHistory();
  }

  showHistory(resetPage?: boolean): void {
    const objType = this.selectFrom === 'LAST_VIEWED'
      ? _.get(this, ['lastViewedObject', 'objType', 'searchObject']) || _.get(this, ['lastViewedObject', 'objType', 'type'])
      : _.get(this, ['objectType', 'searchObject']) || _.get(this, ['objectType', 'type']);
    const objId = this.selectFrom === 'LAST_VIEWED' ? _.get(this, ['lastViewedObject', 'id']) : this.objectId;

    if (!objId || !objType) {
      this.page.clear();
      return;
    }
    this.page.load({id: objId, type: objType}, !!resetPage);
  }

  clipboardText(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }
    return typeof value === 'string' ? value : JSON.stringify(value);
  }

  private parseVersionHistory(data: any): any[] {
    let prevVersion: any = {};
    return _.reverse(_.map([...(data.results || [])].reverse(), (result: any) => {
      let currentVersion: any;
      try {
        currentVersion = JSON.parse(result.json);
      } catch {
        prevVersion = {};
        return {
          user: result.user || null,
          masqueraderUser: result.masqueraderUser || null,
          timestamp: result.timestamp,
          diff: '<p>undefined</p>',
          json: '<p>undefined</p>'
        };
      }

      if (_.isEmpty(prevVersion) && result.previousJson) {
        prevVersion = JSON.parse(result.previousJson);
      }
      const diff = this.ObjectDiff.diff(prevVersion, currentVersion);
      const versionData = {
        user: result.user || null,
        masqueraderUser: result.masqueraderUser || null,
        timestamp: result.timestamp,
        diff: this.ObjectDiff.toJsonDiffView(diff),
        json: this.ObjectDiff.toJsonView(diff),
        diffRaw: _.filter(_.map(_.get(diff, 'value'), (val: any, key: string) => {
          if (_.isPlainObject(val)) {
            val.field = key;
          }
          return val;
        }), (val: any) => _.get(val, 'changed') !== 'equal'),
        jsonRaw: currentVersion
      };

      prevVersion = currentVersion;
      return versionData;
    }));
  }
}
